import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useProducts } from '@/modules/products/hooks/useProducts';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { Input } from '@/shared/components/ui/input';
import { isApiError } from '@/shared/lib/apiClient';
import { SubscriptionEventAnalyticsSection } from '../components/SubscriptionEventAnalyticsSection';
import { SubscriptionEventDetailDrawer } from '../components/SubscriptionEventDetailDrawer';
import {
  SubscriptionEventKpiCards,
  SubscriptionEventKpiCardsSkeleton,
} from '../components/SubscriptionEventKpiCards';
import { SubscriptionEventMobileCard } from '../components/SubscriptionEventMobileCard';
import { SubscriptionEventSourceBadge } from '../components/SubscriptionEventSourceBadge';
import { SubscriptionEventTypeBadge } from '../components/SubscriptionEventTypeBadge';
import {
  SUBSCRIPTION_EVENT_DEFAULT_LIMIT,
  SUBSCRIPTION_EVENT_SOURCES,
  SUBSCRIPTION_EVENT_TYPES,
} from '../constants';
import { useSubscriptionEventAnalytics } from '../hooks/useSubscriptionEventAnalytics';
import { useSubscriptionEventHistory } from '../hooks/useSubscriptionEventHistory';
import type { SubscriptionEventHistoryItem, SubscriptionEventHistoryQuery } from '../types';
import {
  computeSubscriptionEventKpis,
  formatDateTime,
  resolveQueryErrorMessage,
} from '../utils/helpers';

export function SubscriptionEventsDashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;
  const canReadAnalytics = user?.permissions.includes('analytics.read') ?? false;
  const canAccess = isSuperAdmin && canReadAnalytics;

  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [eventSourceFilter, setEventSourceFilter] = useState('');
  const [tenantIdInput, setTenantIdInput] = useState('');
  const [productIdInput, setProductIdInput] = useState('');
  const [subscriptionIdInput, setSubscriptionIdInput] = useState('');
  const [appliedTenantId, setAppliedTenantId] = useState('');
  const [appliedProductId, setAppliedProductId] = useState('');
  const [appliedSubscriptionId, setAppliedSubscriptionId] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<SubscriptionEventHistoryItem | null>(null);

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard', replace: true });
    }
  }, [user, isSuperAdmin, navigate]);

  const serverQuery = useMemo((): SubscriptionEventHistoryQuery => {
    const query: SubscriptionEventHistoryQuery = { limit: SUBSCRIPTION_EVENT_DEFAULT_LIMIT };
    if (eventTypeFilter) {
      query.eventType = eventTypeFilter as NonNullable<SubscriptionEventHistoryQuery['eventType']>;
    }
    if (eventSourceFilter) {
      query.eventSource = eventSourceFilter as NonNullable<
        SubscriptionEventHistoryQuery['eventSource']
      >;
    }
    if (appliedTenantId.trim()) query.tenantId = appliedTenantId.trim();
    if (appliedProductId.trim()) query.productId = appliedProductId.trim();
    if (appliedSubscriptionId.trim()) query.subscriptionId = appliedSubscriptionId.trim();
    return query;
  }, [
    eventTypeFilter,
    eventSourceFilter,
    appliedTenantId,
    appliedProductId,
    appliedSubscriptionId,
  ]);

  const historyQuery = useSubscriptionEventHistory(serverQuery, canAccess);
  const analyticsQuery = useSubscriptionEventAnalytics(canAccess);
  const { data: productsData } = useProducts({ limit: 100 });

  const productLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of productsData?.items ?? []) {
      const label = i18n.language === 'ar' ? product.name_ar || product.name_en : product.name_en;
      map.set(product.id, label);
    }
    return map;
  }, [productsData?.items, i18n.language]);

  const events = useMemo(() => historyQuery.data ?? [], [historyQuery.data]);
  const kpis = useMemo(
    () => computeSubscriptionEventKpis(analyticsQuery.data),
    [analyticsQuery.data],
  );

  const refetchAll = useCallback(() => {
    void historyQuery.refetch();
    void analyticsQuery.refetch();
  }, [historyQuery, analyticsQuery]);

  const applySearch = () => {
    setAppliedTenantId(tenantIdInput);
    setAppliedProductId(productIdInput);
    setAppliedSubscriptionId(subscriptionIdInput);
  };

  const resetFilters = () => {
    setEventTypeFilter('');
    setEventSourceFilter('');
    setTenantIdInput('');
    setProductIdInput('');
    setSubscriptionIdInput('');
    setAppliedTenantId('');
    setAppliedProductId('');
    setAppliedSubscriptionId('');
  };

  const isFetching = historyQuery.isFetching || analyticsQuery.isFetching;
  const isInitialLoading =
    canAccess &&
    (historyQuery.isLoading || analyticsQuery.isLoading) &&
    !historyQuery.data &&
    !analyticsQuery.data;

  const unavailable = t('subscriptionEvents.common.unavailable');

  const resolveProductLabel = useCallback(
    (productId: string | null) => {
      if (!productId) return null;
      return productLabelById.get(productId) ?? null;
    },
    [productLabelById],
  );

  const columns: ColumnDef<SubscriptionEventHistoryItem>[] = useMemo(
    () => [
      {
        key: 'occurredAt',
        header: t('subscriptionEvents.columns.eventTime'),
        accessor: (event) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDateTime(event.occurredAt, locale)}
          </span>
        ),
        sortValue: (event) => event.occurredAt,
      },
      {
        key: 'eventType',
        header: t('subscriptionEvents.columns.eventType'),
        accessor: (event) => <SubscriptionEventTypeBadge eventType={event.eventType} />,
        sortValue: (event) => event.eventType,
        align: 'center',
      },
      {
        key: 'eventSource',
        header: t('subscriptionEvents.columns.eventSource'),
        accessor: (event) => <SubscriptionEventSourceBadge eventSource={event.eventSource} />,
        sortValue: (event) => event.eventSource,
        align: 'center',
      },
      {
        key: 'tenant',
        header: t('subscriptionEvents.columns.tenant'),
        accessor: (event) => (
          <TrialTruncatedText
            value={event.tenantId}
            fallback={unavailable}
            monospace
            maxWidthClass="max-w-[9rem]"
          />
        ),
        sortValue: (event) => event.tenantId ?? '',
      },
      {
        key: 'product',
        header: t('subscriptionEvents.columns.product'),
        accessor: (event) => (
          <TrialTruncatedText
            value={resolveProductLabel(event.productId) ?? event.productId}
            fallback={unavailable}
            maxWidthClass="max-w-[9rem]"
          />
        ),
        sortValue: (event) => resolveProductLabel(event.productId) ?? event.productId ?? '',
      },
      {
        key: 'subscription',
        header: t('subscriptionEvents.columns.subscription'),
        accessor: (event) => (
          <TrialTruncatedText
            value={event.subscriptionId}
            fallback={unavailable}
            monospace
            maxWidthClass="max-w-[9rem]"
          />
        ),
        sortValue: (event) => event.subscriptionId ?? '',
      },
      {
        key: 'status',
        header: t('subscriptionEvents.columns.status'),
        accessor: (event) => (
          <TrialTruncatedText
            value={event.eventStatus}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (event) => event.eventStatus ?? '',
        align: 'center',
      },
      {
        key: 'actions',
        header: t('subscriptionEvents.columns.actions'),
        accessor: (event) => (
          <div className="flex justify-end whitespace-nowrap shrink-0">
            <Button variant="outline" size="sm" onClick={() => setSelectedEvent(event)}>
              <Icons.Eye className="h-3.5 w-3.5" />
              {t('common.view')}
            </Button>
          </div>
        ),
        align: 'right',
      },
    ],
    [t, locale, unavailable, resolveProductLabel],
  );

  if (!user || !isSuperAdmin) {
    return null;
  }

  if (!canReadAnalytics) {
    return (
      <div className="space-y-6">
        <PageHero
          title={t('subscriptionEvents.title')}
          subtitle={t('subscriptionEvents.subtitle')}
          icon={<Icons.Repeat className="h-7 w-7" aria-hidden />}
        />
        <EmptyPanel
          title={t('subscriptionEvents.errors.forbidden')}
          message={t('subscriptionEvents.errors.missingPermission')}
        />
      </div>
    );
  }

  const historyErrorMessage = historyQuery.error
    ? resolveQueryErrorMessage(historyQuery.error, t, isApiError)
    : null;
  const analyticsErrorMessage = analyticsQuery.error
    ? resolveQueryErrorMessage(analyticsQuery.error, t, isApiError)
    : null;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHero
        title={t('subscriptionEvents.title')}
        subtitle={t('subscriptionEvents.subtitle')}
        eyebrow={t('subscriptionEvents.eyebrow')}
        icon={<Icons.Repeat className="h-7 w-7" aria-hidden />}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={refetchAll}
              disabled={isFetching}
            >
              <Icons.RefreshCw
                className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                aria-hidden
              />
              <span className="ms-2">{t('common.refresh')}</span>
            </Button>
            <span className="text-xs text-muted-foreground self-center">
              {t('subscriptionEvents.autoRefreshHint')}
            </span>
          </>
        }
      />

      {isInitialLoading ? (
        <SubscriptionEventKpiCardsSkeleton />
      ) : analyticsQuery.isError && !analyticsQuery.data ? (
        <ErrorPanel message={analyticsErrorMessage ?? t('subscriptionEvents.errors.loadFailed')} />
      ) : (
        <SubscriptionEventKpiCards kpis={kpis} />
      )}

      <SubscriptionEventAnalyticsSection
        analytics={analyticsQuery.data}
        isLoading={analyticsQuery.isLoading && !analyticsQuery.data}
        isError={analyticsQuery.isError}
        {...(analyticsErrorMessage ? { errorMessage: analyticsErrorMessage } : {})}
      />

      {analyticsQuery.isError && analyticsQuery.data ? (
        <ErrorPanel message={analyticsErrorMessage ?? t('subscriptionEvents.errors.loadFailed')} />
      ) : null}

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-1.5">
            <label
              htmlFor="subscription-event-type-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('subscriptionEvents.filters.eventType')}
            </label>
            <select
              id="subscription-event-type-filter"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('subscriptionEvents.filters.allEventTypes')}</option>
              {SUBSCRIPTION_EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`subscriptionEvents.eventTypes.${type}`, type)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="subscription-event-source-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('subscriptionEvents.filters.eventSource')}
            </label>
            <select
              id="subscription-event-source-filter"
              value={eventSourceFilter}
              onChange={(e) => setEventSourceFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('subscriptionEvents.filters.allEventSources')}</option>
              {SUBSCRIPTION_EVENT_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {t(`subscriptionEvents.eventSources.${source}`, source)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="subscription-event-tenant-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('subscriptionEvents.filters.tenantId')}
            </label>
            <Input
              id="subscription-event-tenant-filter"
              value={tenantIdInput}
              onChange={(e) => setTenantIdInput(e.target.value)}
              placeholder={t('subscriptionEvents.filters.tenantIdPlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="subscription-event-product-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('subscriptionEvents.filters.productId')}
            </label>
            <Input
              id="subscription-event-product-filter"
              value={productIdInput}
              onChange={(e) => setProductIdInput(e.target.value)}
              placeholder={t('subscriptionEvents.filters.productIdPlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="subscription-event-subscription-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('subscriptionEvents.filters.subscriptionId')}
            </label>
            <Input
              id="subscription-event-subscription-filter"
              value={subscriptionIdInput}
              onChange={(e) => setSubscriptionIdInput(e.target.value)}
              placeholder={t('subscriptionEvents.filters.subscriptionIdPlaceholder')}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={applySearch}>
            {t('subscriptionEvents.filters.search')}
          </Button>
          <Button type="button" variant="outline" onClick={resetFilters}>
            {t('subscriptionEvents.filters.reset')}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">{t('subscriptionEvents.filters.hint')}</p>
      </div>

      {historyQuery.isLoading && !historyQuery.data ? (
        <LoadingPanel />
      ) : historyQuery.isError && !historyQuery.data ? (
        <div className="space-y-3">
          <ErrorPanel message={historyErrorMessage ?? t('subscriptionEvents.errors.loadFailed')} />
          <div className="flex justify-center">
            <Button type="button" variant="outline" size="sm" onClick={refetchAll}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      ) : events.length === 0 ? (
        <EmptyPanel
          title={t('subscriptionEvents.empty.title')}
          message={t('subscriptionEvents.empty.message')}
        />
      ) : (
        <>
          <div className="hidden md:block">
            <DataTable<SubscriptionEventHistoryItem>
              data={events}
              columns={columns}
              rowKey={(event) => event.id}
              total={events.length}
              initialPageSize={10}
              emptyMessage={t('subscriptionEvents.empty.title')}
            />
          </div>
          <div className="md:hidden space-y-3">
            {events.map((event) => (
              <SubscriptionEventMobileCard
                key={event.id}
                event={event}
                locale={locale}
                productLabel={resolveProductLabel(event.productId)}
                onView={() => setSelectedEvent(event)}
              />
            ))}
          </div>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        {t('subscriptionEvents.footer.showing', {
          shown: events.length,
          limit: SUBSCRIPTION_EVENT_DEFAULT_LIMIT,
        })}
      </p>

      {selectedEvent ? (
        <SubscriptionEventDetailDrawer
          event={selectedEvent}
          productLabel={resolveProductLabel(selectedEvent.productId)}
          onClose={() => setSelectedEvent(null)}
        />
      ) : null}
    </div>
  );
}
